/**
 * pago-directo.ts — ap-pagar-factura-directo (T1).
 * Request / response contracts of paying ONE comprobante directly (no lote, no signatures):
 *   GET  /sumi/cuentas-pagar/comprobantes/:id/pago-sugerido?tipoCambio=&fecha=   -> PagoSugeridoResponse
 *   POST /sumi/cuentas-pagar/comprobantes/:id/pagar                       -> PagoDirectoResponse
 *
 * The backend computes everything (suggested retentions, A PAGAR): the client only renders and edits.
 * Money in RESPONSES is a decimal string (never `Number()`'d on the wire); retention amounts are ARS (what is
 * deposited to the tax authority), `pendiente` / `aPagar` are in the comprobante currency.
 */
import { z } from 'zod';
import { CurrencyCodeSchema, roundToCents } from '@suite/contracts-kernel';
import type { CurrencyCode } from '@suite/contracts-kernel';
import { IsoDateSchema, MedioPagoSchema } from './vocab.js';

// ---------------------------------------------------------------------------
// GET pago-sugerido
// ---------------------------------------------------------------------------

/** `fecha` = the payment date the per-supplier rates are taken at (default: today). */
export const PagoSugeridoQuerySchema = z.object({
  tipoCambio: z.coerce.number().positive().optional(),
  fecha: IsoDateSchema.optional(),
  /**
   * Editing a payment (`PUT /pagos/:id`): the `pago` movement being replaced. Its amount still applied to this invoice
   * counts as pending again, as it will once the edit annuls it.
   */
  editarPago: z.string().uuid().optional(),
});
export type PagoSugeridoQuery = z.infer<typeof PagoSugeridoQuerySchema>;

export const PAGO_SUGERENCIA_FUENTES = ['override', 'tipo', 'proveedor', 'manual', 'sin_base'] as const;
export const PagoSugerenciaFuenteSchema = z.enum(PAGO_SUGERENCIA_FUENTES);

export const PagoSugeridoComprobanteSchema = z.object({
  id: z.string().uuid(),
  proveedorId: z.string().uuid(),
  proveedorNombre: z.string(),
  /** `FAC A 0001-00000012` */
  comprobanteLabel: z.string(),
  moneda: CurrencyCodeSchema,
  montoTotal: z.string(),
  montoNeto: z.string().nullable(),
  /** Current pending, in the comprobante currency. */
  pendiente: z.string(),
  /** The exchange rate stored on the comprobante, if any. */
  tipoCambio: z.string().nullable(),
  /** ISO due date; `null` when the comprobante has none (the approval PDF's "Vence"). Optional: older responses lack it. */
  fechaVencimiento: z.string().nullable().optional(),
  /** "Detalle de pago" typed on the invoice (migration 0041), printed under the invoice number. Optional like the above. */
  detallePago: z.string().nullable().optional(),
});

export const PagoSugeridoRetencionSchema = z.object({
  tipoRetencionId: z.string().uuid(),
  nombre: z.string(),
  /** ARS; `null` = no suggestion (manual type, or no base: foreign comprobante without TC). */
  monto: z.string().nullable(),
  fuente: PagoSugerenciaFuenteSchema,
  /** The rate (a percentage) that decided the suggestion: the supplier's own, else the type's; `null` for an exempt supplier, a fixed amount or a manual type. */
  alicuota: z.number().nullable(),
});

export const PagoSugeridoResponseSchema = z.object({
  comprobante: PagoSugeridoComprobanteSchema,
  /** The TC the suggestion was computed with (query, else the comprobante's own); `null` for ARS or when unknown. */
  tipoCambioUsado: z.string().nullable(),
  /** `true` when the comprobante is foreign and no TC is known: nothing can be suggested yet. */
  requiereTipoCambio: z.boolean(),
  /** One entry per ACTIVE retention type. */
  retenciones: z.array(PagoSugeridoRetencionSchema),
  /** Σ of the suggested amounts, ARS. */
  totalRetencionesArs: z.string(),
  /** pendiente − Σ retenciones / TC, comprobante currency. `null` while `requiereTipoCambio`. */
  aPagar: z.string().nullable(),
});
export type PagoSugeridoResponse = z.infer<typeof PagoSugeridoResponseSchema>;

// ---------------------------------------------------------------------------
// POST pagar
// ---------------------------------------------------------------------------

export const PagoDirectoRetencionSchema = z.object({
  tipoRetencionId: z.string().uuid(),
  /** ARS; `0` = "no retention" of this type. */
  montoArs: z.number().nonnegative(),
});

export const PagoDirectoRequestSchema = z
  .object({
    fechaPago: IsoDateSchema,
    medioPago: MedioPagoSchema,
    /** Required when the comprobante is not in ARS. */
    tipoCambio: z.number().positive().optional(),
    /** Comprobante currency; defaults to the whole current pending. */
    importeACancelar: z.number().positive().optional(),
    retenciones: z.array(PagoDirectoRetencionSchema).default([]),
  })
  .refine((v) => new Set(v.retenciones.map((r) => r.tipoRetencionId)).size === v.retenciones.length, {
    message: 'Una retención por tipo.',
    path: ['retenciones'],
  });
export type PagoDirectoRequest = z.input<typeof PagoDirectoRequestSchema>;
export type PagoDirectoInput = z.output<typeof PagoDirectoRequestSchema>;

export const PagoDirectoResponseSchema = z.object({
  ordenPagoId: z.string().uuid(),
  numero: z.number().int().positive(),
  proveedorId: z.string().uuid(),
  moneda: CurrencyCodeSchema,
  pagoMovimientoId: z.string().uuid(),
  retencionMovimientoIds: z.array(z.string().uuid()),
  /** Comprobante currency. */
  importeCancelado: z.string(),
  /** What leaves the bank, ARS. */
  totalPagadoArs: z.string(),
});
export type PagoDirectoResponse = z.infer<typeof PagoDirectoResponseSchema>;

// ---------------------------------------------------------------------------
// A PAGAR — ONE formula shared by the writer (backend) and the live recompute of the screen (frontend).
// ---------------------------------------------------------------------------

export interface CalcularAPagarInput {
  /** Current pending, in the comprobante currency. */
  readonly pendiente: number;
  readonly moneda: CurrencyCode;
  /** Exchange rate; ignored for ARS. */
  readonly tipoCambio: number | null;
  /** The retentions, ARS. Zero / negative amounts count as "no retention". */
  readonly retencionesArs: readonly number[];
}

/**
 * `aPagar = pendiente − Σ round2(retencionArs / TC)` (TC = 1 for ARS), comprobante currency. Every retention is
 * converted and rounded on its own before summing — the same rule `buildOrdenPagoPlan` writes to the ledger.
 * `null` for a foreign currency without a usable TC.
 */
export function calcularAPagar(input: CalcularAPagarInput): number | null {
  const tc = input.moneda === 'ARS' ? 1 : input.tipoCambio;
  if (tc === null || !(tc > 0)) return null;
  const retencionesCc = input.retencionesArs
    .filter((m) => m > 0)
    .reduce((sum, m) => sum + roundToCents(m / tc), 0);
  return roundToCents(input.pendiente - retencionesCc);
}
